import React, { useEffect, useState } from 'react';
import { useGetLiveArtists } from '../../hooks/useGetLiveArtists';
import LocalisedDatePicker from '../Common/LocalisedDatePicker';
import LocalisedTimePicker from '../Common/LocalisedTimePicker';
import {
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { liveArtistsState } from '../../state/RecoilState';
import { useRecoilValue } from 'recoil';
import { Artist, Booking } from '../../@types/api';
import { Dayjs } from 'dayjs';
import RestUtilities from '../../services/RestUtilities';
import { bookingStatus, dbDayFormat, eventTypes } from '../../Constants';

const sxPropsL = { my: 1, mr: '2%', width: '48%' };
const sxPropsR = { my: 1, width: '50%' };
const sxPropsFull = { width: '100%' };

interface Props {
  booking: Booking;
  saveChanges: (booking: any) => void;
  buttonText: string;
}
const BookingDetails: React.FC<Props> = ({
  booking,
  saveChanges,
  buttonText,
}) => {
  const artists = useRecoilValue(liveArtistsState);
  const [bookingChanges, setBookingChanges] = useState({});
  const [unavailableArtists, setUnavailableArtists] = useState<number[]>([]);
  const displayBooking: Booking = { ...booking, ...bookingChanges };

  useGetLiveArtists();

  useEffect(() => {
    const getUnavailable = async () => {
      const res = await RestUtilities.get(
        `/api/enquiry/GetUnavailable?eventDate=${displayBooking.eventDate.format(
          dbDayFormat
        )}`
      );
      if (res.ok) {
        let artistsUnavailable: number[] = res.content;
        if (displayBooking.statusId === bookingStatus.CONFIRMED) {
          artistsUnavailable = artistsUnavailable.filter(
            (x) => x !== displayBooking.artistId
          );
        }
        setUnavailableArtists(artistsUnavailable);
      }
    };
    if (!unavailableArtists.length && !!displayBooking.eventDate) {
      getUnavailable();
    }
  }, [
    displayBooking.artistId,
    displayBooking.eventDate,
    displayBooking.statusId,
    unavailableArtists,
  ]);

  const changeBooking = ({
    field,
    changes,
  }: {
    field: string;
    changes: any;
  }) => {
    const nextChanges = {
      ...bookingChanges,
      [field]: changes,
    };
    setBookingChanges(nextChanges);
    if (field === 'eventDate') {
      setUnavailableArtists([]);
    }
  };

  const changeArtistBooking = (artistId: number) => {
    const found = artists.find((a: Artist) => a.artistId === artistId);
    const nextChanges = {
      ...bookingChanges,
      artistId: artistId,
      artist: found,
    };
    setBookingChanges(nextChanges);
  };

  return (
    <Grid container spacing={2}>
      <Grid item sm={12} md={6}>
        <div>
          <FormControl required sx={sxPropsL}>
            <InputLabel id='status'>Status</InputLabel>
            <Select
              labelId='status'
              id='status-label'
              label='Status'
              value={displayBooking.statusId}
              size='small'
              onChange={(event) => {
                changeBooking({
                  field: 'statusId',
                  changes: event.target.value,
                });
              }}
              inputProps={{ readOnly: displayBooking.bookingId ? false : true }}
            >
              <MenuItem value={bookingStatus.OPEN}>New</MenuItem>
              <MenuItem value={bookingStatus.ONHOLD}>On Hold</MenuItem>
              <MenuItem value={bookingStatus.CONFIRMED}>Confirmed</MenuItem>
              <MenuItem value={bookingStatus.CANCELLED}>Cancelled</MenuItem>
              <MenuItem value={bookingStatus.COMPLETE}>Complete</MenuItem>
            </Select>
          </FormControl>
          <FormControl required sx={sxPropsR}>
            <LocalisedDatePicker
              label='EventDate'
              date={displayBooking.eventDate}
              setDate={(date: Dayjs | null) => {
                changeBooking({
                  field: 'eventDate',
                  changes: date,
                });
              }}
            />
          </FormControl>
        </div>
        <div>
          <FormControl required sx={sxPropsL}>
            <LocalisedTimePicker
              label='StartTime'
              time={displayBooking.entStartTime}
              setTime={(date: Dayjs) => {
                changeBooking({
                  field: 'entStartTime',
                  changes: date,
                });
              }}
            />
          </FormControl>
          <FormControl required sx={sxPropsR}>
            <LocalisedTimePicker
              label='EndTime'
              time={displayBooking.entEndTime}
              setTime={(date: Dayjs) => {
                changeBooking({
                  field: 'entEndTime',
                  changes: date,
                });
              }}
            />
          </FormControl>
        </div>
      </Grid>
      <Grid item sm={12} md={6}>
        <div>
          <FormControl required sx={sxPropsL}>
            <InputLabel id='event-type'>EventType</InputLabel>
            <Select
              size='small'
              labelId='event-type'
              id='event-type-label'
              label='EventType'
              value={displayBooking.eventTypeId}
              onChange={(event) => {
                changeBooking({
                  field: 'eventTypeId',
                  changes: event.target.value,
                });
              }}
            >
              <MenuItem value={eventTypes.WEDDING}>Wedding</MenuItem>
              <MenuItem value={eventTypes.BIRTHDAY}>Birthday</MenuItem>
              <MenuItem value={eventTypes.CORPORATE}>
                Corporate Entertainment
              </MenuItem>
              <MenuItem value={eventTypes.OTHER}>Other</MenuItem>
              <MenuItem value={eventTypes.MFSPUBGIG}>Brewhemia</MenuItem>
            </Select>
          </FormControl>

          <FormControl required sx={sxPropsR}>
            <InputLabel id='artist-label'>Artist</InputLabel>
            <Select
              size='small'
              labelId='artist-label'
              id='artist-select'
              label='Artist'
              autoWidth
              value={displayBooking.artistId || -1}
              onChange={(event) => {
                changeArtistBooking(Number(event.target.value));
              }}
            >
              <MenuItem key={-1} value={-1}>
                Please Select
              </MenuItem>
              {artists &&
                artists
                  .filter(
                    (a: Artist) => !unavailableArtists.includes(a.artistId)
                  )
                  .map((a: Artist) => (
                    <MenuItem key={a.artistId} value={a.artistId}>
                      {a.artistName}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </div>
        <div>
          <FormControl sx={sxPropsL}>
            <TextField
              size='small'
              required
              id='venue'
              label='VenueName'
              value={displayBooking.venueName}
              onChange={(event) => {
                changeBooking({
                  field: 'venueName',
                  changes: event.target.value,
                });
              }}
            />
          </FormControl>
          <FormControl sx={sxPropsR}>
            <TextField
              size='small'
              required
              id='location'
              label='Location'
              value={displayBooking.location}
              onChange={(event) => {
                changeBooking({
                  field: 'location',
                  changes: event.target.value,
                });
              }}
            />
          </FormControl>
        </div>
      </Grid>
      <Grid item sm={12} md={6}>
        <FormControl sx={sxPropsFull}>
          <TextField
            multiline
            id='clientComment'
            label='Client Contract Notes'
            value={displayBooking.contractNote ?? ''}
            fullWidth
            margin='none'
            onChange={(event) => {
              changeBooking({
                field: 'contractNote',
                changes: event.target.value,
              });
            }}
            InputProps={{
              rows: 4,
              multiline: true,
              inputComponent: 'textarea',
            }}
            variant='outlined'
          />
        </FormControl>
        <FormControlLabel
          disabled
          control={
            <Checkbox
              checked={displayBooking.isContractSigned}
              name='contractSent'
              color='primary'
            />
          }
          label='Client Contract Sent'
        />
      </Grid>
      <Grid item sm={12} md={6}>
        <FormControl sx={sxPropsFull}>
          <TextField
            multiline
            id='clientComment'
            label='Artist Booking Form Notes'
            value={displayBooking.comments ?? ''}
            fullWidth
            margin='none'
            onChange={(event) => {
              changeBooking({
                field: 'comments',
                changes: event.target.value,
              });
            }}
            InputProps={{
              rows: 4,
              multiline: true,
              inputComponent: 'textarea',
            }}
            variant='outlined'
          />
        </FormControl>
        <FormControlLabel
          disabled
          control={
            <Checkbox
              checked={displayBooking.isBandContractSigned}
              name='contractSent'
              color='primary'
            />
          }
          label='Artist Contract Sent'
        />
      </Grid>
      <Grid item sm={12} md={6}>
        <Button
          variant='contained'
          color='primary'
          onClick={() => saveChanges(displayBooking)}
          disabled={displayBooking.artistId < 1}
        >
          {buttonText}
        </Button>
      </Grid>
    </Grid>
  );
};
export default BookingDetails;
